import { useForm, useLiveData } from 'hooks';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { number, object } from 'yup';
import { ONLY_APPROVED_ARTISTS_QUERY } from 'app-constants';

import { useGlobalModalContext } from '../../../../../context/GlobalModal';
import { requestPayout } from '../../../../../services';
import { Button, Input, Modal, SelectForm, Splash } from '../../../../../shared/Components';
import { FlexContainer, Text } from '../../../../../shared/Typhography';

interface CreatePayoutForm {
  amount: number;
  artist: { value: string; label: string } | null;
}

interface CreatePayoutModalProps {
  refetch?(): void;
}

const createPayoutSchema = object({
  amount: number()
    .required('Amount is required')
    .not([0], 'Request amount can not be 0')
    .transform((value) => (isNaN(value) ? undefined : value)), // Prevents "" is NaN message when input is empty.
  artist: object({}).optional().nullable(),
});

export default function CreatePayoutModal({ refetch }: CreatePayoutModalProps) {
  const { hideModal } = useGlobalModalContext();
  const { docs: artistsDocuments, state: artistsFetchState } = useLiveData(
    ONLY_APPROVED_ARTISTS_QUERY,
  );
  const [options, setOptions] = useState<any>([]);

  const [isLoading, setLoading] = useState<boolean>(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreatePayoutForm>({
    schema: createPayoutSchema,
    defaultValues: {
      artist: null,
    },
  });

  const submit = handleSubmit(async (data: CreatePayoutForm) => {
    if (!data?.amount || !data.artist) {
      toast.error('Empty requests are not allowed');
      return;
    }

    if (data?.amount == 0) {
      toast.error('Request amount can not be 0');
      return;
    }

    setLoading(true);

    try {
      await requestPayout({
        artistId: data.artist?.value,
        requestedAmount: +data.amount,
      });

      if (refetch) refetch();

      hideModal();
      toast.success('Payout request created.');
    } catch (err: any) {
      toast.error(err.message ?? String(err));
    }

    setLoading(false);
  });

  const prepareOptions = () => {
    setOptions(
      artistsDocuments
        ?.filter((x) => x.data().Name || x.data().username)
        ?.map((item) => {
          return {
            value: item.id,
            label: `${item.data().Name ?? '-'} ${
              item.data().username ? `(${item.data().username})` : ''
            }`,
          };
        }),
    );
  };

  useEffect(() => {
    if (artistsFetchState === 'success') prepareOptions();
  }, [artistsFetchState]);

  return (
    <Modal onRequestClose={hideModal} height={500}>
      <Text style={{ width: '100%' }} weight="bold" size={1.5} textAlign="center">
        Create Payout request
      </Text>
      <form
        onSubmit={submit}
        style={{
          padding: '10px',
          marginBottom: 'auto',
          marginTop: '20px',
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        <SelectForm
          name="artist"
          error={errors.artist}
          disabled={artistsFetchState === 'loading'}
          placeholder="Select Artist"
          control={control}
          values={options}
          width="100%"
          required
        />
        <Input
          error={errors.amount}
          type="number"
          name="amount"
          control={control}
          placeholder={'Enter amount'}
          width="100%"
          margin="40px 0 0 0"
          step="0.01"
          required
        />
      </form>

      <FlexContainer width="100%" justifyContent="space-evenly">
        {isLoading && <Splash position="relative" />}
        {!isLoading && (
          <>
            <Button onClick={() => hideModal()}>
              <Text color="white" padding="5px" size={1.2} cursor="pointer" weight="bold">
                Cancel
              </Text>
            </Button>
            <Button onClick={() => submit()}>
              <Text color="white" padding="5px" size={1.2} cursor="pointer" weight="bold">
                Create
              </Text>
            </Button>
          </>
        )}
      </FlexContainer>
    </Modal>
  );
}
